import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/seo/SEO";
import { useAuth } from "@/context/Auth";
import { supabase } from "@/integrations/supabase/client";
import { RoleDebug } from "@/components/RoleDebug";
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import csvProLogo from "@/assets/csv-pro-logo-new.png";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { useBrandingLogo } from "@/hooks/useBrandingLogo";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import MfaSecurityCta from "@/components/auth/MfaSecurityCta";
import MfaStatusBanner from "@/components/auth/MfaStatusBanner";

interface AppLayoutProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  /** When true, suppress the hierarchical branding logo in the global header */
  hideBrandingLogo?: boolean;
}

const navItems = [
  { to: "/wizard", label: "Assessment" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/reports", label: "Reports" },
  { to: "/admin", label: "Admin" },
];

// client_admin users have no top-level nav items - they use their dashboard directly

const AppLayout = ({ title, description, children, hideBrandingLogo = false }: AppLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, roles } = useAuth();
  const { role: effectiveRole } = useEffectiveRole();
  const { logoUrl: brandingLogo, loading: brandingLoading } = useBrandingLogo();
  
  // PM pages show their own org context header with logos — don't duplicate in global header
  const isPMRole = effectiveRole === "portfolio_manager";
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Role-based redirects are now handled by RoleGate at "/" 
  // Only log here for debugging

  const visibleNavItems = (() => {
    // Admin users don't need nav items - they use the Admin Console tabs
    if (roles?.includes("admin")) return [];
    // Client admin users have no nav items - they use their dashboard directly
    if (effectiveRole === "client_admin") return [];
    if (roles?.includes("reviewer")) return navItems.filter(n => ["/dashboard","/reports"].includes(n.to));
    if (roles?.includes("respondent")) return []; // No nav items for respondents
    // PO and Approver roles don't have standard nav items - they access specific assessments
    return [];
  })();

  // Check if assessment has started for respondents
  const assessmentStarted = (() => {
    try {
      const state = JSON.parse(localStorage.getItem("cyber-assess-state") || "{}");
      return state?.responses?.some((r: any) => r.score !== null && r.score !== undefined) || false;
    } catch {
      return false;
    }
  })();

  const primaryPath = roles?.includes("admin")
    ? "/admin"
    : roles?.includes("reviewer")
    ? "/reviewer"
    : "/wizard";

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    
    try {
      setIsLoggingOut(true);
      
      // Clear any cached auth tokens first
      localStorage.removeItem("supabase.auth.token");
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace("https://", "").replace(".supabase.co", "");
      localStorage.removeItem(`sb-${supabaseUrl}-auth-token`);
      
      // Clear any application-specific data
      localStorage.removeItem("cyber-assess-state");
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Force navigation to login
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      // Even if logout fails, clear local state and redirect
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-subtle-gradient">
      <SEO title={title} description={description} canonical={location.pathname} />
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-semibold flex items-center gap-3">
              <img src={csvProLogo} alt="CSV Pro" className="h-24 w-auto" />
            </Link>
            {!isPMRole && !hideBrandingLogo && (
              brandingLoading ? (
                <Skeleton className="h-12 w-32 rounded" />
              ) : brandingLogo && brandingLogo !== csvProLogo ? (
                <BrandedLogo
                  src={brandingLogo}
                  alt="Organization"
                  className="h-12 w-auto"
                />
              ) : null
            )}
          </div>
          <nav className="hidden md:flex items-center gap-2" aria-label="Main navigation">
            {visibleNavItems.map((n) => (
              <Link key={n.to} to={n.to} className="">
                <Button 
                  variant={location.pathname === n.to ? "default" : "soft"} 
                  size="sm" 
                  aria-current={location.pathname === n.to ? "page" : undefined}
                >
                  {n.label}
                </Button>
              </Link>
            ))}
          </nav>
          {/* Show logout for all authenticated users except on landing/auth pages */}
          {session && location.pathname !== "/" && location.pathname !== "/auth" && (
            <div className="flex items-center gap-2">
              <MfaSecurityCta />
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging out..." : "Logout"}
              </Button>
            </div>
          )}
        </div>
      </header>
      {session && location.pathname !== "/" && location.pathname !== "/auth" && (
        <MfaStatusBanner />
      )}
      {import.meta.env.DEV && (
        <RoleDebug />
      )}
      <main className="container flex-1 py-8">
        {children}
      </main>
      <footer className="border-t py-6">
        <div className="container text-sm text-muted-foreground">
          <div className="text-center">© {new Date().getFullYear()} CSV Pro</div>
          {session && (
            <div className="flex justify-center mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Signing Out..." : "Sign Out"}
              </Button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default AppLayout;
